// src/pages/profile/AccountSettings.tsx
import { logger } from '../../utils/logger';

import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonToggle,
  IonAlert,
  IonToast,
  IonIcon,
  IonNote,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import {
  logOutOutline,
  trashOutline,
  cardOutline,
  sparkles,
  moonOutline,
  sunnyOutline,
  colorPaletteOutline,
  checkmarkCircleOutline,
  helpCircleOutline,
  shieldCheckmarkOutline,
  downloadOutline,
} from 'ionicons/icons';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import { profileService } from '../../services/profileService';
import { requestAccountDeletion, getPendingDeletionRequest, cancelAccountDeletion } from '../../services/deleteAccountService';
import { downloadDataExport, getExportDataSummary } from '../../services/dataExportService';
import { usePremium } from '../../hooks/usePremium';
import { themeService, ThemeMode, THEME_PRESETS } from '../../services/themeService';
import ThemeCustomizer from '../../components/ThemeCustomizer';
import { AccessibilitySettings } from '../../components/AccessibilitySettings';
import { SoundSettings } from '../../components/SoundSettings';
import { CookieSettings } from '../../components/CookieSettings';
import { Capacitor } from '@capacitor/core';
import { useOnboarding } from '../../components/OnboardingTutorial';
import { updateDataWithRetry } from '../../services/retryService';
import { handleError } from '../../services/errorHandler';
import './AccountSettings.css';

const AccountSettings: React.FC = () => {
  const history = useHistory();
  const { isPremium, loading: premiumLoading } = usePremium();
  const { resetOnboarding } = useOnboarding();
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pushEnabled, setPushEnabled] = useState(true);
  const [themeMode, setThemeMode] = useState<ThemeMode>('system');
  const [currentThemePreset, setCurrentThemePreset] = useState(themeService.getThemePreset());
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [forceRenderKey, setForceRenderKey] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [showChangeEmailAlert, setShowChangeEmailAlert] = useState(false);
  const [showChangePasswordAlert, setShowChangePasswordAlert] = useState(false);
  const [showDeletePasswordAlert, setShowDeletePasswordAlert] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showDeleteConfirmAlert, setShowDeleteConfirmAlert] = useState(false);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportInfoAlert, setShowExportInfoAlert] = useState(false);
  const [hasPendingDeletion, setHasPendingDeletion] = useState(false);
  const [pendingDeletionDate, setPendingDeletionDate] = useState<string>('');

  useEffect(() => {
    loadUserData();
    setThemeMode(themeService.getThemeMode());
    setCurrentThemePreset(themeService.getThemePreset());

    setTimeout(() => {
      themeService.forceRefresh();
    }, 0);
  }, []);

  useEffect(() => {
    const handleThemeModeChange = () => {
      setThemeMode(themeService.getThemeMode());
      setCurrentThemePreset(themeService.getThemePreset());
    };

    const handleThemePresetChange = () => {
      setCurrentThemePreset(themeService.getThemePreset());
    };

    window.addEventListener('theme-mode-changed', handleThemeModeChange);
    window.addEventListener('theme-preset-changed', handleThemePresetChange);

    return () => {
      window.removeEventListener('theme-mode-changed', handleThemeModeChange);
      window.removeEventListener('theme-preset-changed', handleThemePresetChange);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        themeService.forceRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const loadUserData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setEmail(user.email || '');

      // Check for pending deletion request
      const { data: pendingDeletion } = await supabase
        .from('account_deletion_requests')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (pendingDeletion) {
        setHasPendingDeletion(true);
        setPendingDeletionDate(pendingDeletion.delete_after);
      }
    }

    const profile = await profileService.getCurrentProfile();
    if (profile) {
      setPushEnabled(profile.push_enabled ?? true);
    }
  };

  const handleChangeEmail = async (newEmailAddr?: string, currentPwd?: string) => {
    const emailAddr = newEmailAddr || newEmail;
    const pwd = currentPwd || currentPassword;

    if (!emailAddr.trim() || !pwd.trim()) {
      showToastMessage('Please fill in all fields', 'danger');
      return;
    }

    setLoading(true);

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: pwd,
    });

    if (signInError) {
      showToastMessage('Current password is incorrect', 'danger');
      setLoading(false);
      return;
    }

    // Use deep link for mobile platforms, web URL for web
    const isNative = Capacitor.isNativePlatform();

    // For web: Use VITE_APP_URL if set (production), otherwise use current origin (local dev)
    const webBaseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

    const redirectUrl = isNative
      ? 'com.dicecat.app://auth/callback'
      : `${webBaseUrl}/auth-success.html`;

    const { error } = await supabase.auth.updateUser(
      {
        email: emailAddr,
      },
      {
        emailRedirectTo: redirectUrl
      }
    );

    if (error) {
      showToastMessage(error.message, 'danger');
    } else {
      showToastMessage('Verification email sent to new address. Please check your inbox.', 'success');
      setNewEmail('');
      setCurrentPassword('');
    }

    setLoading(false);
    setShowChangeEmailAlert(false);
  };

  const handleChangePassword = async (current?: string, newPass?: string, confirmPass?: string) => {
    const currentPwd = current || currentPassword;
    const newPwd = newPass || newPassword;
    const confirmPwd = confirmPass || confirmPassword;

    if (!currentPwd.trim() || !newPwd.trim() || !confirmPwd.trim()) {
      showToastMessage('Please fill in all fields', 'danger');
      return;
    }

    if (newPwd !== confirmPwd) {
      showToastMessage('New passwords do not match', 'danger');
      return;
    }

    if (newPwd.length < 6) {
      showToastMessage('Password must be at least 6 characters', 'danger');
      return;
    }

    setLoading(true);

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPwd,
    });

    if (signInError) {
      showToastMessage('Current password is incorrect', 'danger');
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: newPwd,
    });

    if (error) {
      showToastMessage(error.message, 'danger');
    } else {
      showToastMessage('Password updated successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }

    setLoading(false);
    setShowChangePasswordAlert(false);
  };

  const handleTogglePushNotifications = async (enabled: boolean) => {
    setPushEnabled(enabled);

    try {
      const result = await updateDataWithRetry(
        () => profileService.updateProfile({
          push_enabled: enabled,
        })
      );

      if (result.success) {
        showToastMessage(
          enabled ? 'Push notifications enabled' : 'Push notifications disabled',
          'success'
        );
      } else {
        setPushEnabled(!enabled);
        showToastMessage('Failed to update notification settings', 'danger');
      }
    } catch (error) {
      setPushEnabled(!enabled);
      const appError = handleError(error, 'update notification settings');
      showToastMessage(appError.userMessage, 'danger');
    }
  };

  const handleThemeModeChange = async (mode: ThemeMode) => {
    setIsTransitioning(true);

    const currentIsDark = document.body.classList.contains('dark');
    const bgColor = currentIsDark ? '#111827' : '#ffffff';
    const spinnerColor = currentIsDark ? '#ffffff' : '#000000';
    const spinnerBorder = currentIsDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';

    const loadingDiv = document.createElement('div');
    loadingDiv.id = 'theme-transition-overlay';
    loadingDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${bgColor};
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;

    // Create spinner element programmatically (safer than innerHTML)
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 80px;
      height: 80px;
      border: 4px solid ${spinnerBorder};
      border-top-color: ${spinnerColor};
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `;

    // Create style element for animation
    const style = document.createElement('style');
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';

    loadingDiv.appendChild(spinner);
    loadingDiv.appendChild(style);
    document.body.appendChild(loadingDiv);

    await new Promise(resolve => setTimeout(resolve, 50));

    setThemeMode(mode);
    await themeService.setThemeMode(mode);
    setCurrentThemePreset(themeService.getThemePreset());

    document.body.classList.add('theme-changing');
    void document.body.offsetHeight;
    await new Promise(resolve => setTimeout(resolve, 300));
    document.body.classList.remove('theme-changing');

    themeService.forceRefresh();
    setForceRenderKey(prev => prev + 1);

    await new Promise(resolve => setTimeout(resolve, 200));
    loadingDiv.remove();
    setIsTransitioning(false);

    showToastMessage('Theme mode updated', 'success');
  };

  const handleThemeCustomizerDismiss = () => {
    setShowThemeCustomizer(false);
    setCurrentThemePreset(themeService.getThemePreset());
  };

  const handleDeleteAccount = async (password?: string) => {
    if (!password || !password.trim()) {
      showToastMessage('Password is required to delete your account', 'danger');
      return;
    }

    setDeleting(true);
    setShowDeleteConfirmAlert(false);

    try {
      // Request account deletion with password verification
      const result = await updateDataWithRetry(
        () => requestAccountDeletion(password)
      );

      if (result.success) {
        setDeletePassword(''); // Clear password
        showToastMessage(
          'Account deletion requested. You have 30 days to cancel before permanent deletion.',
          'success'
        );
        // Don't sign out - user can cancel within grace period
        // Show them the deletion status
        setTimeout(() => {
          showToastMessage('Check your email for verification link', 'success');
        }, 3500);
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to request account deletion';
        showToastMessage(errorMsg, 'danger');
        setDeleting(false);
        setDeletePassword(''); // Clear password on error
      }
    } catch (error) {
      const appError = handleError(error, 'request account deletion');
      showToastMessage(appError.userMessage, 'danger');
      setDeleting(false);
      setDeletePassword(''); // Clear password on error
    }
  };

  const handleCancelDeletion = async () => {
    try {
      const result = await updateDataWithRetry(
        () => cancelAccountDeletion()
      );

      if (result.success) {
        showToastMessage('Account deletion cancelled. Your account is safe!', 'success');
        setHasPendingDeletion(false);
        setPendingDeletionDate('');
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to cancel deletion';
        showToastMessage(errorMsg, 'danger');
      }
    } catch (error) {
      const appError = handleError(error, 'cancel account deletion');
      showToastMessage(appError.userMessage, 'danger');
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    setShowExportInfoAlert(false);

    try {
      const result = await downloadDataExport();

      if (result.success) {
        showToastMessage('Your data has been downloaded successfully!', 'success');
      } else {
        showToastMessage(result.error || 'Failed to export data', 'danger');
      }
    } catch (error: any) {
      logger.error('Export error:', error);
      showToastMessage(error.message || 'Failed to export data', 'danger');
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    history.push('/login');
  };

  const handleResetTutorial = () => {
    resetOnboarding();
    showToastMessage('Tutorial reset! It will show next time you open the app.', 'success');
    // Optionally redirect to home to trigger tutorial immediately
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const showToastMessage = (message: string, color: 'success' | 'danger') => {
    setToastMessage(message);
    setToastColor(color);
    setShowToast(true);
  };

  const currentThemeName = THEME_PRESETS[currentThemePreset].name;

  return (
    <IonPage key={forceRenderKey}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/profile" />
          </IonButtons>
          <IonTitle>Account Settings</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="account-settings-page">
        <div className="settings-wrapper">
        <IonList>
          <IonListHeader>
            <IonLabel>Account Information</IonLabel>
          </IonListHeader>

          <IonItem>
            <IonLabel>
              <h2>Email</h2>
              <p>{email}</p>
            </IonLabel>
            <IonButton
              fill="outline"
              size="small"
              onClick={() => setShowChangeEmailAlert(true)}
            >
              Change
            </IonButton>
          </IonItem>

          <IonItem>
            <IonLabel>
              <h2>Password</h2>
            </IonLabel>
            <IonButton
              fill="outline"
              size="small"
              onClick={() => setShowChangePasswordAlert(true)}
            >
              Change
            </IonButton>
          </IonItem>
        </IonList>

        <IonList>
          <IonListHeader>
            <IonLabel>Subscription</IonLabel>
          </IonListHeader>

          <IonItem button onClick={() => history.push('/settings/subscription')}>
            <IonIcon icon={cardOutline} slot="start" />
            <IonLabel>
              <h2>Manage Subscription</h2>
              <p>View and manage your Dice Cat Plus subscription</p>
            </IonLabel>
            {!premiumLoading && isPremium && (
              <IonIcon icon={sparkles} slot="end" color="warning" />
            )}
          </IonItem>
        </IonList>

        <IonList>
          <IonListHeader>
            <IonLabel>Appearance</IonLabel>
          </IonListHeader>

          <IonItem>
            <IonIcon icon={themeMode === 'dark' ? moonOutline : themeMode === 'system' ? (systemPrefersDark ? moonOutline : sunnyOutline) : sunnyOutline} slot="start" />
            <IonLabel>
              <h2>Dark Mode</h2>
              <p>Choose light, dark, or system default</p>
            </IonLabel>
            <IonSelect
              value={themeMode}
              interface="action-sheet"
              onIonChange={(e) => handleThemeModeChange(e.detail.value)}
              disabled={isTransitioning}
            >
              <IonSelectOption value="light">Light</IonSelectOption>
              <IonSelectOption value="dark">Dark</IonSelectOption>
              <IonSelectOption value="system">System</IonSelectOption>
            </IonSelect>
          </IonItem>

          <IonItem
            button
            onClick={() => {
              if (isPremium) {
                setShowThemeCustomizer(true);
              } else {
                showToastMessage('Theme customization is a Plus feature', 'danger');
              }
            }}
          >
            <IonIcon icon={colorPaletteOutline} slot="start" />
            <IonLabel>
              <h2>Theme</h2>
              <p>{currentThemeName}</p>
            </IonLabel>
            {!isPremium && (
              <IonIcon icon={sparkles} slot="end" color="warning" />
            )}
          </IonItem>
        </IonList>

        {/* Accessibility Settings */}
        <AccessibilitySettings />

        {/* Sound & Haptics Settings */}
        <SoundSettings />

        {/* Privacy & Data - Combined GDPR Features */}
        <IonList>
          <IonListHeader>
            <IonLabel>Privacy & Data</IonLabel>
          </IonListHeader>

          <IonItem button onClick={() => setShowCookieSettings(true)}>
            <IonIcon icon={shieldCheckmarkOutline} slot="start" />
            <IonLabel>
              <h2>Cookie Settings</h2>
              <p>Manage your cookie preferences and consent</p>
            </IonLabel>
          </IonItem>

          <IonItem
            button
            onClick={() => setShowExportInfoAlert(true)}
            disabled={exporting}
          >
            <IonIcon icon={downloadOutline} slot="start" />
            <IonLabel>
              <h2>{exporting ? 'Exporting...' : 'Export My Data'}</h2>
              <p>Download all your personal data (GDPR compliant)</p>
            </IonLabel>
          </IonItem>

          <IonItem>
            <IonLabel>
              <h2>Push Notifications</h2>
              <p>Get notified about new messages and connection requests</p>
            </IonLabel>
            <IonToggle
              checked={pushEnabled}
              onIonChange={(e) => handleTogglePushNotifications(e.detail.checked)}
            />
          </IonItem>
        </IonList>

        <IonList>
          <IonListHeader>
            <IonLabel>Account Actions</IonLabel>
          </IonListHeader>

          <IonItem button onClick={handleResetTutorial}>
            <IonIcon icon={helpCircleOutline} slot="start" />
            <IonLabel>
              <h2>Restart Tutorial</h2>
              <p>See the onboarding tutorial again</p>
            </IonLabel>
          </IonItem>

          <IonItem button onClick={() => setShowLogoutAlert(true)}>
            <IonIcon icon={logOutOutline} slot="start" />
            <IonLabel>
              <h2>Log Out</h2>
            </IonLabel>
          </IonItem>
        </IonList>

        <IonList style={{ marginTop: '40px' }}>
          <IonListHeader className={hasPendingDeletion ? 'warning-zone-header' : 'danger-zone-header'}>
            <IonLabel>{hasPendingDeletion ? 'Pending Deletion' : 'Danger Zone'}</IonLabel>
          </IonListHeader>

          {hasPendingDeletion ? (
            <IonItem
              button
              onClick={handleCancelDeletion}
              lines="none"
            >
              <IonIcon icon={checkmarkCircleOutline} slot="start" color="success" />
              <IonLabel color="success">
                <h2>Cancel Account Deletion</h2>
                <p style={{ color: 'var(--ion-text-color)' }}>
                  {pendingDeletionDate
                    ? `Scheduled for deletion on ${new Date(pendingDeletionDate).toLocaleDateString()}. Click to cancel.`
                    : 'You have a pending deletion request. Click to cancel it.'
                  }
                </p>
              </IonLabel>
            </IonItem>
          ) : (
            <IonItem
              button
              onClick={() => setShowDeleteAlert(true)}
              disabled={deleting}
              lines="none"
            >
              <IonIcon icon={trashOutline} slot="start" color="danger" />
              <IonLabel color="danger">
                <h2>{deleting ? 'Processing...' : 'Delete Account'}</h2>
                <p style={{ color: 'var(--ion-text-color)' }}>
                  Permanently delete your account and all data
                </p>
              </IonLabel>
            </IonItem>
          )}
        </IonList>
        </div>

        <div style={{ padding: '20px', paddingBottom: '60px', textAlign: 'center' }}>
          <IonNote color="medium" style={{ fontSize: '12px' }}>
            Need help? Contact support at support@dice-cat.com
          </IonNote>
        </div>

        <ThemeCustomizer
          isOpen={showThemeCustomizer}
          onDismiss={handleThemeCustomizerDismiss}
        />

        <CookieSettings
          isOpen={showCookieSettings}
          onDismiss={() => setShowCookieSettings(false)}
          onSaved={() => showToastMessage('Cookie preferences updated', 'success')}
        />

        <IonAlert
          isOpen={showChangeEmailAlert}
          onDidDismiss={() => setShowChangeEmailAlert(false)}
          header="Change Email"
          message="Enter your new email address and current password"
          inputs={[
            {
              name: 'newEmail',
              type: 'email',
              placeholder: 'New email address',
              value: newEmail,
            },
            {
              name: 'currentPassword',
              type: 'password',
              placeholder: 'Current password',
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Change Email',
              handler: (data) => {
                handleChangeEmail(data.newEmail, data.currentPassword);
                return false;
              },
            },
          ]}
        />

        <IonAlert
          isOpen={showChangePasswordAlert}
          onDidDismiss={() => setShowChangePasswordAlert(false)}
          header="Change Password"
          message="Enter your current password and choose a new one"
          inputs={[
            {
              name: 'currentPassword',
              type: 'password',
              placeholder: 'Current password',
            },
            {
              name: 'newPassword',
              type: 'password',
              placeholder: 'New password (min 6 characters)',
            },
            {
              name: 'confirmPassword',
              type: 'password',
              placeholder: 'Confirm new password',
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Change Password',
              handler: (data) => {
                handleChangePassword(data.currentPassword, data.newPassword, data.confirmPassword);
                return false;
              },
            },
          ]}
        />

        <IonAlert
          isOpen={showLogoutAlert}
          onDidDismiss={() => setShowLogoutAlert(false)}
          header="Log Out"
          message="Are you sure you want to log out?"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Log Out',
              handler: handleLogout,
            },
          ]}
        />

        <IonAlert
          isOpen={showExportInfoAlert}
          onDidDismiss={() => setShowExportInfoAlert(false)}
          header="📦 Export Your Data"
          message={
            'This will download a JSON file containing all your personal data, including:\n\n' +
            getExportDataSummary().map(item => `• ${item}`).join('\n') +
            '\n\nThis complies with GDPR Article 20 (Right to Data Portability).'
          }
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Download',
              handler: handleExportData,
            },
          ]}
        />

        <IonAlert
          isOpen={showDeletePasswordAlert}
          onDidDismiss={() => {
            setShowDeletePasswordAlert(false);
            setDeletePassword('');
          }}
          header="Confirm Password"
          message="Enter your password to continue with account deletion request"
          inputs={[
            {
              name: 'password',
              type: 'password',
              placeholder: 'Your password',
              value: deletePassword,
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                setDeletePassword('');
              },
            },
            {
              text: 'Continue',
              handler: (data) => {
                setDeletePassword(data.password);
                setShowDeletePasswordAlert(false);
                setTimeout(() => setShowDeleteAlert(true), 300);
              },
            },
          ]}
        />

        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="⚠️ Request Account Deletion?"
          message={
            'Requesting deletion will:\n\n' +
            '• Start a 30-day grace period\n' +
            '• Send you a verification email\n' +
            '• Allow you to cancel at any time\n\n' +
            'After 30 days, this will permanently delete:\n' +
            '• Your profile and all personal information\n' +
            '• All your games and availability\n' +
            '• All your connections and messages\n' +
            '• All your matches and chat history\n\n' +
            'This action cannot be undone after the grace period!'
          }
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                setDeletePassword(''); // Clear password on cancel
              },
            },
            {
              text: 'Continue',
              role: 'destructive',
              handler: () => {
                setShowDeleteAlert(false);
                setTimeout(() => setShowDeleteConfirmAlert(true), 300);
              },
            },
          ]}
        />

        <IonAlert
          isOpen={showDeleteConfirmAlert}
          onDidDismiss={() => setShowDeleteConfirmAlert(false)}
          header="Confirm Your Password"
          message="To proceed with account deletion, please enter your password. You'll receive an email to verify, then have 30 days to change your mind."
          inputs={[
            {
              name: 'password',
              type: 'password',
              placeholder: 'Your password',
              attributes: {
                autocomplete: 'current-password',
              },
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                setDeletePassword('');
              },
            },
            {
              text: deleting ? 'Requesting...' : 'Yes, Request Deletion',
              role: 'destructive',
              handler: (data) => {
                handleDeleteAccount(data.password);
                return false; // Prevent alert from closing until we handle it
              },
            },
          ]}
        />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
          aria-live="polite"
        />
      </IonContent>
    </IonPage>
  );
};

export default AccountSettings;